import type { StateCreator } from 'zustand'

import {
  CHAT_SIDEBAR_MIN_WIDTH_PX,
  CHAT_SIDEBAR_SAFE_MARGIN_PX,
  CONTENT_MIN_WIDTH_PX,
  XL_BREAKPOINT_PX,
} from '@/config/constants'

export interface ChatSidebar {
  open: boolean
  width: number
}

export interface ChatSidebarSlice {
  chatSidebar: ChatSidebar
  setChatSidebarOpen: (open: boolean) => void
  setChatSidebarWidth: (width: number) => void
}

/**
 * Widest the chat panel may get at the current viewport. Tier-aware, because
 * the two tiers cost the content area different things:
 *
 *  - >=xl the panel IS grid column 3, so every pixel it takes comes out of the
 *    centre column. Mirrors the CSS `--chat-inline-width` clamp in AuthLayout:
 *    viewport - `--app-sidebar-width` (3.5rem = 56px, left as a literal so the CSS
 *    stays the sole source) - `--content-min-width` (40rem = 640px).
 *  - below xl the panel floats over the content, which keeps its full width,
 *    so only the safe margin caps it.
 *
 * Exported so the drag handle's aria-valuemax and the resize clamp report the
 * same number the store will actually enforce.
 */
export function chatSidebarMaxWidth(): number {
  if (typeof window === 'undefined') return Infinity
  return window.innerWidth >= XL_BREAKPOINT_PX
    ? window.innerWidth - 56 - CONTENT_MIN_WIDTH_PX
    : window.innerWidth - CHAT_SIDEBAR_SAFE_MARGIN_PX
}

function clampWidth(width: number): number {
  return Math.max(
    CHAT_SIDEBAR_MIN_WIDTH_PX,
    Math.min(chatSidebarMaxWidth(), width)
  )
}

export const createChatSidebarSlice: StateCreator<
  ChatSidebarSlice,
  [],
  [],
  ChatSidebarSlice
> = (set) => ({
  chatSidebar: { open: true, width: CHAT_SIDEBAR_MIN_WIDTH_PX },
  setChatSidebarOpen: (open) =>
    set((s) => ({ chatSidebar: { ...s.chatSidebar, open } })),
  setChatSidebarWidth: (width) =>
    set((s) => ({
      chatSidebar: { ...s.chatSidebar, width: clampWidth(width) },
    })),
})
